import { useMemo, useState } from 'react';

import { useLeagueContentRenderer } from '@/feature/lobby/hooks/use-league-content-render';
import { useSocialFeedAndEventsData } from '@/feature/social-feed/use-social-feed';

import { FILTER_MODES_TABS, FilterType, Filters } from '../utils/filters';
import { useLobbyData } from './LobbyDataProvider';
import { useLobbyContentRender } from './use-lobby-content-render';
import { useSortedListOfPlayers } from './use-sorted-lobby-players';

/**
 * Hook to compute filter-dependent data for the lobby screen.
 * Consumes pre-fetched data from LobbyDataProvider and applies player filters/sorting.
 * Only re-runs when filters change or when the provider data updates.
 */
export const useFantasyLobbyContent = () => {
    const {
        sortedLeagueEvents,
        sortedLobbyPlayers,
        sortedGroupedUFCPlayers,
        leagueEventsLoading,
        hasStaleData,
        leagueId,
        isLobby,
        positionFilters,
    } = useLobbyData();

    const [filterType, setFilterMode] = useState<FilterType>(FILTER_MODES_TABS[0].filterType);

    const { sortedPlayers: sortedListOfPlayers, popularProjectionsPlayers } = useSortedListOfPlayers({
        sortedEvents: sortedLeagueEvents,
        filterType,
        positionFilters,
    });

    //Compute sorted players as empty if we have stale data, to avoid showing stale players while switching leagues
    const sortedPlayers = useMemo(() => {
        return hasStaleData ? [] : sortedListOfPlayers;
    }, [hasStaleData, sortedListOfPlayers]);

    const socialFeedVisible = !isLobby && filterType === Filters.SocialLineups;

    const { data: socialFeedEntries, fetching: fetchingSocialFeed } = useSocialFeedAndEventsData({
        enabled: socialFeedVisible,
        league: leagueId,
    });

    const {
        renderPlayerItem,
        renderUFCFightRow,
        renderEventCards,
        renderFilters,
        renderLeagueFooter,
        renderSocialFeedItem,
    } = useLeagueContentRenderer({
        league: leagueId,
        selectedFilter: filterType,
        selectFilter: setFilterMode,
        sortedEvents: sortedLeagueEvents,
        socialFeedEntries,
        sortedPlayers,
        positionFilters,
        eventsLoading: leagueEventsLoading,
        socialLineupsLoading: fetchingSocialFeed,
        popularProjectionsPlayers,
    });

    const { renderLobbyPlayerItem, renderLobbyComponent } = useLobbyContentRender({ players: sortedLobbyPlayers });

    return {
        sortedPlayers,
        sortedLobbyPlayers,
        sortedGroupedUFCPlayers,
        socialFeedEntries,
        renderLobbyPlayerItem,
        renderLobbyComponent,
        renderPlayerItem,
        renderUFCFightRow,
        renderEventCards,
        renderFilters,
        renderLeagueFooter,
        renderSocialFeedItem,
        filterType,
        isLobby,
    };
};
